/**
 * PsicoSalud — Ficha del paciente y consentimientos informados (Módulo 2).
 *
 * Los consentimientos no son un trámite decorativo: el backend bloquea
 * el análisis con IA si el consentimiento AI_ANALYSIS no está vigente,
 * conforme a la Ley N° 7018/2022. Esta pantalla es donde se otorgan y
 * se revocan.
 */

import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { useAuth } from '@/context/AuthContext';

import { patientsApi } from '@/api';
import Skeleton from '@/components/Skeleton';
import PatientAppointments from '@/components/PatientAppointments';
import type { Consent, ConsentType, Patient } from '@/types';

const CONSENT_TYPES: ConsentType[] = [
  'TREATMENT',
  'DATA_PROCESSING',
  'AI_ANALYSIS',
  'VIDEO_SESSION',
  'DATA_SHARING',
];

export default function PatientDetailPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { patientId = '' } = useParams();

  const [patient, setPatient] = useState<Patient | null>(null);
  const [consents, setConsents] = useState<Consent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyConsent, setBusyConsent] = useState<ConsentType | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [detail, consentList] = await Promise.all([
        patientsApi.get(patientId),
        patientsApi.listConsents(patientId),
      ]);
      setPatient(detail);
      setConsents(consentList);
      setError(null);
    } catch {
      setError(t('errors.notFound'));
    } finally {
      setLoading(false);
    }
  }, [patientId, t]);

  useEffect(() => {
    void load();
  }, [load]);

  const toggleConsent = async (type: ConsentType, grant: boolean) => {
    setBusyConsent(type);
    try {
      await patientsApi.registerConsent(patientId, {
        consent_type: type,
        is_granted: grant,
        consent_text: t(`consent.types.${type}`),
      });
      await load();
    } catch {
      setError(t('errors.generic'));
    } finally {
      setBusyConsent(null);
    }
  };

  const downloadFhir = async () => {
    const resource = await patientsApi.exportFhir(patientId);
    const blob = new Blob([JSON.stringify(resource, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `paciente-${patientId}-fhir.json`;
    link.click();
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <>
        <header className="page-header with-flex" aria-hidden="true">
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--espacio-4)' }}>
            <Skeleton variant="circle" size={64} />
            <div style={{ flex: 1, maxWidth: '420px' }}>
              <Skeleton variant="caption" width="30%" />
              <div style={{ height: 'var(--espacio-2)' }} />
              <Skeleton variant="heading" width="55%" />
              <div style={{ height: 'var(--espacio-1)' }} />
              <Skeleton variant="text" width="40%" />
            </div>
          </div>
        </header>

        <div className="card">
          <div className="card-header">
            <h2>{t('patients.title')}</h2>
          </div>
          <div className="grid grid-3">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i}>
                <Skeleton variant="caption" width="40%" />
                <div style={{ height: 'var(--espacio-1)' }} />
                <Skeleton variant="text" width="75%" />
              </div>
            ))}
          </div>
        </div>

        <div className="card">
          <div className="card-header">
            <h2>{t('consent.title')}</h2>
          </div>
          <Skeleton.Table rows={4} columns={3} />
        </div>
      </>
    );
  }
  if (!patient) return <div className="notice notice-error">{error ?? t('errors.notFound')}</div>;

  const consentFor = (type: ConsentType) => consents.find((c) => c.consent_type === type);

  const initials = patient.full_name
    .split(' ')
    .map((part) => part.charAt(0))
    .slice(0, 2)
    .join('')
    .toUpperCase();

  return (
    <>
      <header className="page-header with-flex">
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--espacio-4)' }}>
          <div
            aria-hidden="true"
            style={{
              width: '64px',
              height: '64px',
              borderRadius: '50%',
              background: 'var(--principal-claro)',
              color: 'var(--principal-hover)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontFamily: 'var(--fuente-display)',
              fontSize: 'var(--texto-lg)',
              fontWeight: 'var(--peso-semibold)',
              letterSpacing: 'var(--tracking-apretado)',
              flexShrink: 0,
            }}
          >
            {initials}
          </div>
          <div>
            <span className="page-eyebrow">{t('patients.title')}</span>
            <h1 data-cy="patient-name">{patient.full_name}</h1>
            <p className="page-descripcion">
              <span className="t-numerico">{patient.document_id}</span>
              {patient.edad !== null && <> · {patient.edad} años</>}
              {patient.gender && (
                <> · {t(`patients.genders.${patient.gender}`)}</>
              )}
            </p>
          </div>
        </div>
        {user?.role !== 'ADMIN' && (
          <div className="page-acciones">
            <Link
              to={`/pacientes/${patientId}/historial`}
              className="is-button is-secondary is-pequeno"
            >
              {t('record.title')}
            </Link>
            <Link
              to={`/pacientes/${patientId}/evaluaciones`}
              className="is-button is-secondary is-pequeno"
            >
              {t('forms.title')}
            </Link>
            <Link
              to={`/pacientes/${patientId}/indicadores`}
              className="is-button is-pequeno"
            >
              {t('analytics.title')}
            </Link>
          </div>
        )}
      </header>

      {user?.role === 'ADMIN' && (
        <div className="notice notice-info" data-cy="patient-admin-scope">
          {t('patients.adminScope')}
        </div>
      )}

      {error && <div className="notice notice-error">{error}</div>}

      <div className="card">
        <div className="card-header">
          <div>
            <span className="card-eyebrow">{t('patients.title')}</span>
            <h2>{t('patients.documentId')} y datos de contacto</h2>
          </div>
          <button className="secondary is-pequeno" onClick={() => void downloadFhir()} data-cy="patient-export-fhir">
            {t('patients.exportFhir')}
          </button>
        </div>
        <div className="grid grid-3" data-cy="patient-fields">
          <Field label={t('patients.documentId')} value={patient.document_id} />
          <Field label={t('patients.age')} value={patient.edad !== null ? String(patient.edad) : '—'} />
          <Field
            label={t('patients.gender')}
            value={patient.gender ? t(`patients.genders.${patient.gender}`) : '—'}
          />
          <Field label={t('patients.phone')} value={patient.phone ?? '—'} />
          <Field label={t('patients.email')} value={patient.email ?? '—'} />
          <Field label={t('patients.emergencyContact')} value={patient.emergency_contact ?? '—'} />
          <Field label={t('patients.address')} value={patient.address ?? '—'} />
        </div>
      </div>

      <PatientAppointments key={patientId} patientId={patientId} editable />

      <div className="card">
        <div className="card-header">
          <div>
            <span className="card-eyebrow">{t('consent.title')}</span>
            <h2>{t('consent.title')}</h2>
          </div>
          <span className="t-mayuscula" style={{ color: 'var(--tinta-suave)' }}>
            Ley N° 7018/2022
          </span>
        </div>
        <p className="t-pequeno t-suave" style={{ marginBottom: 'var(--espacio-4)' }}>
          {t('consent.description')}
        </p>

        <div className="table-wrapper">
          <table data-cy="consents-table">
            <thead>
              <tr>
                <th>{t('consent.title')}</th>
                <th style={{ width: '10rem' }}>{t('agenda.status')}</th>
                <th style={{ width: '12rem', textAlign: 'right' }}>{t('common.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {CONSENT_TYPES.map((type) => {
                const consent = consentFor(type);
                const active = consent?.vigente ?? false;
                return (
                  <tr key={type} data-cy={`consent-row-${type}`}>
                    <td>
                      <strong>{t(`consent.types.${type}`)}</strong>
                    </td>
                    <td>
                      <span
                        className={`badge ${
                          active
                            ? 'badge-success'
                            : consent
                              ? 'badge-warning'
                              : 'badge-neutral'
                        }`}
                        data-cy={`consent-status-${type}`}
                      >
                        <span className="badge-dot" />
                        {active
                          ? t('consent.granted')
                          : consent
                            ? t('consent.revoked')
                            : t('consent.notRegistered')}
                      </span>
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      <button
                        className={active ? 'secondary is-pequeno' : 'is-pequeno'}
                        disabled={busyConsent === type}
                        onClick={() => void toggleConsent(type, !active)}
                        data-cy={`consent-toggle-${type}`}
                      >
                        {active ? t('consent.revoke') : t('consent.grant')}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="t-mayuscula" style={{ color: 'var(--tinta-suave)', marginBottom: 'var(--espacio-1)' }}>
        {label}
      </div>
      <div className="t-fuerte">{value}</div>
    </div>
  );
}
